import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  List,
  Input,
  Button,
  Space,
  Typography,
  Avatar,
  Badge,
  Tooltip,
  Tag,
  Alert,
  message,
  Spin,
  Empty,
} from 'antd';
import {
  MessageOutlined,
  SendOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ShopOutlined,
  DollarOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface Message {
  id: number;
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  message_body: string;
  status: 'sent' | 'delivered' | 'failed' | 'received';
  cost?: number;
  created_at: string;
  delivered_at?: string;
  read_at?: string;
}

interface SMSConversationThreadProps {
  customerId?: number;
  workOrderId?: number;
  estimateId?: number;
  customerPhone?: string;
  height?: number;
}

const SMSConversationThread: React.FC<SMSConversationThreadProps> = ({
  customerId,
  workOrderId,
  estimateId,
  customerPhone,
  height = 500,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 30000); // Auto-refresh every 30 seconds
    return () => clearInterval(interval);
  }, [customerId, workOrderId, estimateId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      let url = '';
      if (customerId) {
        url = `${API_BASE_URL}/admin/sms/conversations/customer/${customerId}`;
      } else if (workOrderId) {
        url = `${API_BASE_URL}/admin/sms/conversations/work-order/${workOrderId}`;
      } else if (estimateId) {
        url = `${API_BASE_URL}/admin/sms/conversations/estimate/${estimateId}`;
      } else {
        return;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unreadCount || 0);

      // Mark messages as read
      if (response.data.unreadCount > 0) {
        await markMessagesAsRead(response.data.messages);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (msgs: Message[]) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      const unreadMessages = msgs.filter(
        (m) => m.direction === 'inbound' && !m.read_at
      );

      for (const msg of unreadMessages) {
        await axios.post(
          `${API_BASE_URL}/admin/sms/conversations/${msg.id}/read`,
          {},
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      message.warning('Please enter a message');
      return;
    }

    if (!customerPhone && !customerId) {
      message.error('Customer phone number not found');
      return;
    }

    try {
      setSending(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/sms/send`,
        {
          to: customerPhone,
          message: newMessage,
          customerId,
          workOrderId,
          estimateId,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        message.success(`SMS sent! Cost: $${response.data.cost?.toFixed(4) || '0.0075'}`);
        setNewMessage('');
        await loadMessages();
      } else {
        message.error(response.data.message || 'Failed to send SMS');
      }
    } catch (error: any) {
      console.error('Error sending SMS:', error);
      message.error(error.response?.data?.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const getMessageStatusIcon = (msg: Message) => {
    if (msg.direction === 'inbound') {
      return null;
    }

    switch (msg.status) {
      case 'delivered':
        return (
          <Tooltip title="Delivered">
            <CheckOutlined style={{ color: '#52c41a' }} />
          </Tooltip>
        );
      case 'sent':
        return (
          <Tooltip title="Sent">
            <ClockCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        );
      case 'failed':
        return (
          <Tooltip title="Failed">
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const renderMessage = (msg: Message) => {
    const isInbound = msg.direction === 'inbound';
    const timestamp = formatDistanceToNow(new Date(msg.created_at), { addSuffix: true });

    return (
      <div
        key={msg.id}
        style={{
          display: 'flex',
          justifyContent: isInbound ? 'flex-start' : 'flex-end',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            maxWidth: '70%',
            display: 'flex',
            flexDirection: isInbound ? 'row' : 'row-reverse',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <Avatar
            icon={isInbound ? <UserOutlined /> : <ShopOutlined />}
            style={{
              backgroundColor: isInbound ? '#1890ff' : '#52c41a',
              flexShrink: 0,
            }}
          />
          <div style={{ maxWidth: 'calc(100% - 48px)' }}>
            <div
              style={{
                backgroundColor: isInbound ? '#f0f0f0' : '#e6f7ff',
                padding: '12px 16px',
                borderRadius: 12,
                wordWrap: 'break-word',
              }}
            >
              <Text>{msg.message_body}</Text>
            </div>
            <div
              style={{
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: isInbound ? 'flex-start' : 'flex-end',
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {timestamp}
              </Text>
              {getMessageStatusIcon(msg)}
              {msg.cost && (
                <Tooltip title={`Cost: $${msg.cost.toFixed(4)}`}>
                  <Tag
                    icon={<DollarOutlined />}
                    color="green"
                    style={{ fontSize: 10, margin: 0 }}
                  >
                    ${msg.cost.toFixed(4)}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const estimateMessageCost = () => {
    const length = newMessage.length;
    const hasUnicode = /[^\x00-\x7F]/.test(newMessage);
    const segmentLength = hasUnicode ? 70 : 160;
    const segments = Math.ceil(length / segmentLength);
    const cost = segments * 0.0075;
    return { segments, cost };
  };

  const { segments, cost } = newMessage.length > 0 ? estimateMessageCost() : { segments: 0, cost: 0 };

  return (
    <Card
      title={
        <Space>
          <MessageOutlined />
          <Text strong>SMS Conversation</Text>
          {unreadCount > 0 && (
            <Badge count={unreadCount} style={{ backgroundColor: '#52c41a' }} />
          )}
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={loadMessages}
          loading={loading}
        >
          Refresh
        </Button>
      }
      style={{ height: height + 100 }}
    >
      {!customerPhone && !customerId ? (
        <Alert
          message="No Phone Number"
          description="Customer phone number is required to send SMS messages."
          type="warning"
          showIcon
        />
      ) : (
        <>
          {/* Messages Area */}
          <div
            style={{
              height: height - 150,
              overflowY: 'auto',
              marginBottom: 16,
              padding: '8px 0',
            }}
          >
            {loading && messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
              </div>
            ) : messages.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No SMS messages yet"
              />
            ) : (
              <>
                {messages.map(renderMessage)}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Send Message Area */}
          <div>
            <Space.Compact style={{ width: '100%' }}>
              <TextArea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={1600}
                onPressEnter={(e) => {
                  if (e.shiftKey) {
                    return; // Allow shift+enter for new line
                  }
                  e.preventDefault();
                  handleSendMessage();
                }}
                disabled={sending}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={sending}
                disabled={!newMessage.trim()}
                style={{ height: 'auto' }}
              >
                Send
              </Button>
            </Space.Compact>

            {newMessage.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {newMessage.length} / 1600 characters
                  {segments > 1 && ` (${segments} segments)`}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Estimated cost: ${cost.toFixed(4)}
                </Text>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default SMSConversationThread;
